/** Version history: every save, newest first. Restoring writes a new version; nothing is erased. */

import { useEffect, useState, type ReactNode } from 'react';

import { reportsApi } from './reports-api.ts';

const when = new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium', timeStyle: 'short' });

export function VersionsPanel({
  organisationId,
  documentId,
  current,
  onRestore,
  onClose,
}: {
  organisationId: string;
  documentId: string;
  current: number;
  onRestore: (revision: number) => Promise<void>;
  onClose: () => void;
}): ReactNode {
  const [versions, setVersions] = useState<
    { revision: number; note: string | null; created_at: string }[] | null
  >(null);
  const [busy, setBusy] = useState<number | null>(null);
  useEffect(() => {
    reportsApi
      .versions(organisationId, documentId)
      .then(setVersions)
      .catch(() => setVersions([]));
  }, [organisationId, documentId, current]);
  return (
    <aside className="rs" aria-label="Version history">
      <header className="rs-head">
        <strong>Version history</strong>
        <button type="button" className="rb-icon" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      {versions === null ? (
        <p className="rs-explain">Loading…</p>
      ) : (
        <ol className="rv">
          {versions.map((version) => (
            <li key={version.revision} className={version.revision === current ? 'rv-current' : ''}>
              <span>
                <strong>Version {version.revision}</strong>
                <small>
                  {when.format(new Date(version.created_at))}
                  {version.note ? ` · ${version.note}` : ''}
                </small>
              </span>
              {version.revision === current ? (
                <span className="rv-tag">Current</span>
              ) : (
                <button
                  type="button"
                  className="btn small secondary"
                  disabled={busy !== null}
                  onClick={async () => {
                    setBusy(version.revision);
                    await onRestore(version.revision).finally(() => setBusy(null));
                  }}
                >
                  {busy === version.revision ? 'Restoring…' : 'Restore'}
                </button>
              )}
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}
